import CustomImage from "@components/custom-image";
import { productImageUrl } from "@provider/image-url";
import CheckboxIcon from "@components/icons/checkbox-icon";
import ChevronRight from "@components/icons/chevron-right";
import AnimatedLink from "@components/animated-link";
import type { ProductType } from "@type/global";
import { roundness } from "@provider/dripsy";
import type { Wishlist } from "@type/wishlist";
import { Text, useSx, View } from "dripsy";
import AnimatedPressable from "@components/animated-pressable";
import * as React from "react";

import { useIsInWishlist, useWishlistStore } from "./wishlist-store";
import routes from "../../NavigatorRoutes";

function FavouriteItemCustom({
  wishlistItem,
  product,
  isSelectable = false,
}: {
  wishlistItem: Wishlist;
  product?: ProductType;
  isSelectable?: boolean;
}) {
  const sx = useSx();
  const isInWishlist = useIsInWishlist({ wishlistId: wishlistItem.id, productPid: product?.pid });

  const addOrUpdate = useWishlistStore((state) => state.addOrUpdate);
  const productCount = wishlistItem.products.length;

  const addOrRemoveProduct = () => {
    if (!product) {
      return;
    }
    if (isInWishlist) {
      // REMOVE
      addOrUpdate({ product, wishlistId: wishlistItem.id, quantity: 0 });
      return;
    }
    addOrUpdate({ product, wishlistId: wishlistItem.id, quantity: 1 });
  };

  const { InnerComponent, props } = isSelectable
    ? {
        InnerComponent: AnimatedPressable as any,
        props: {
          onPress: addOrRemoveProduct,
        } as React.ComponentProps<typeof AnimatedPressable>,
      }
    : {
        InnerComponent: AnimatedLink as any,
        props: {
          to: routes.FavouriteDetailScreen,
          params: { id: `${wishlistItem.id}` },
        } as React.ComponentProps<typeof AnimatedLink>,
      };

  const pid = wishlistItem.products?.[0]?.pid;
  return (
    <InnerComponent
      {...props}
      animate={React.useMemo(
        () =>
          ({ hovered, pressed }: any) => {
            return {
              backgroundColor: hovered || pressed ? "#f2f2f2" : "#fff",
            };
          },
        []
      )}
      style={sx({
        backgroundColor: "$background",
        borderRadius: roundness,
        flexDirection: "row",
        // flex: 1,
        overflow: "hidden",
        margin: "$1",
        padding: "$2",
        alignContent: "center",
        justifyContent: "space-between",
      })}
    >
      <View sx={{ flexDirection: "row" }}>
        <View
          sx={{
            backgroundColor: "#F7F4E3",
            width: 80,
            height: 80,
            borderRadius: roundness,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <View
            sx={{
              backgroundColor: "$background",
              width: 60,
              height: 60,
              borderRadius: 360,
              overflow: "hidden",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {pid && (
              <CustomImage
                source={{ uri: productImageUrl(pid) }}
                sx={{
                  alignSelf: "center",
                  position: "absolute",

                  width: "60%",
                  height: "60%",
                  borderRadius: roundness,
                  zIndex: -10,
                }}
                alt=""
              />
            )}
          </View>
        </View>
        <View
          sx={{
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <View
            sx={{
              // width: '100%',
              borderRadius: roundness,
              justifyContent: "center",
              maxWidth: 160,
              marginLeft: "$2",
            }}
          >
            <Text sx={{ fontSize: 16, fontWeight: "700" }}>{wishlistItem.name}</Text>
            <Text sx={{ fontSize: 14, fontWeight: "500", color: "$textGrey" }}>
              {productCount} {productCount === 1 ? "product" : "producten"}
            </Text>
          </View>
        </View>
      </View>
      {isSelectable ? (
        <View
          sx={{
            alignSelf: "center",
          }}
        >
          <CheckboxIcon size={28} color="$textGrey" checked={isInWishlist} selectedColor="$primary" />
        </View>
      ) : (
        <View
          sx={{
            position: "absolute",
            right: 6,
            alignSelf: "center",
          }}
        >
          <ChevronRight width={14} height={14} color="$textGrey" />
        </View>
      )}
    </InnerComponent>
  );
}
export default FavouriteItemCustom;
